import { debounce } from 'es-toolkit';
import React from 'react';
import Box from '@zg-rentals/multibrand-ui/lib/Box';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import ProblemCircleFillIcon from '@zg-rentals/multibrand-ui/lib/icons/ProblemCircleFill';
import { Tab, TabList, TabPanel, Tabs } from '@zg-rentals/multibrand-ui/lib/Tabs';
import TransactionRows from './modules/TransactionRows';
import TransactionDownloadButton from './TransactionDownloadButton';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import { DEBOUNCE_WAIT_TIME } from '@common/constants';
import './index.scss';
import type {
  AllTransactionInterface,
  IFeaturePermissionModel,
  IPropertyInfoModel,
  ITransactionModel,
} from '@rental-manager/models/Types';
import type { EditMoveInCostsInterface } from '@rental-manager/pages/Dashboard/types';
import ONE_TIME_PAYMENT_TYPE from '@common/constants/oneTimePaymentTypes';
import { logError } from '@zg-rentals/log-error';

type Props = {
  upcomingTransactions: Array<ITransactionModel>;
  completedTransactions: Array<ITransactionModel>;
  pastDueTransactions: Array<ITransactionModel>;
  upcomingTotal: number;
  completedTotal: number;
  pastDueTotal: number;
  userEvent: Record<string, any>;
  featurePermissions: IFeaturePermissionModel;
  currentTerm: IPropertyInfoModel;
  nextTerm: IPropertyInfoModel;
  allTransactions: AllTransactionInterface;
  resendInviteToRenter: ({ tenantEmail }: any) => Promise<any>;
  requestRemindRenterPayRent: ({ tenantEmail }: any) => Promise<any>;
  remindTenantToPayMIC: ({ tenantEmail }: any) => Promise<any>;
  remindTenantToPayOneTimePayment: ({ tenantEmail, requestKey }: any) => Promise<any>;
  cancelOneTimePayment: ({
    otpKey,
    tenantFirstName,
    paymentType,
    paymentPlanInstallmentTitle,
  }: Record<string, string>) => Promise<any>;
  goToCancelMoveInCostsModal: ({ term, rentTermsKey }: EditMoveInCostsInterface) => Promise<void>;
  trackValidationError: (data: any) => void;
  listingAlias: string;
  goToSetupPaymentPlan: () => void;
  paymentPlanEnabled: boolean;
  propertyId?: string;
  rentSchedulePropertyId?: string;
};

class LedgerSection extends React.Component<Props> {
  private initialTabIndex: number;
  constructor(props: Props) {
    super(props);
    this.initialTabIndex = this.getDefaultTab();
  }

  static UPCOMING_TAB_INDEX = 0;
  static COMPLETED_TAB_INDEX = 1;
  static PASTDUE_TAB_INDEX = 2;

  getDefaultTab = (): number => {
    const { upcomingTransactions, completedTransactions, pastDueTransactions } = this.props.allTransactions;
    if (upcomingTransactions.length > 0) {
      return LedgerSection.UPCOMING_TAB_INDEX;
    } else if (completedTransactions.length > 0) {
      return LedgerSection.COMPLETED_TAB_INDEX;
    } else if (pastDueTransactions.length > 0) {
      return LedgerSection.PASTDUE_TAB_INDEX;
    } else {
      return LedgerSection.UPCOMING_TAB_INDEX;
    }
  };

  renderUpcomingTitle = (): React.ReactNode => {
    const { upcomingTotal } = this.props.allTransactions;
    const tabAmount = upcomingTotal ? centsToDollars(upcomingTotal) : '—';
    return (
      <Tab>
        <div className="Dashboard-TabTextActive">
          <Text type="body-small">Upcoming</Text>
          <Text type="h3" className="Dashboard-Tab-TextAmount">
            {tabAmount}
          </Text>
        </div>
      </Tab>
    );
  };

  renderCompletedTitle = (): React.ReactNode => {
    const { completedTotal } = this.props.allTransactions;
    const tabAmount = completedTotal ? centsToDollars(completedTotal) : '—';
    return (
      <Tab>
        <div className="Dashboard-TabTextActive">
          <Text type="body-small">Completed</Text>
          <Text type="h3" className="Dashboard-Tab-TextAmount">
            {tabAmount}
          </Text>
        </div>
      </Tab>
    );
  };

  renderPastDueTitle = (): React.ReactNode => {
    const { pastDueTransactions, pastDueTotal } = this.props.allTransactions;
    const tabAmount = pastDueTotal ? centsToDollars(pastDueTotal) : '—';
    return (
      <Tab>
        <div className="Dashboard-TabTextActive">
          <Text type="body-small" inline>
            Past due
          </Text>
          {pastDueTransactions.length > 0 && <ProblemCircleFillIcon className="Payments-pastDueIcon" size="sm" />}
          <Text type="h3" className="Dashboard-Tab-TextAmount">
            {tabAmount}
          </Text>
        </div>
      </Tab>
    );
  };

  renderErrorState = (): React.ReactNode => {
    logError({
      error: new Error('Landlord dashboard ledger failed to load.'),
      context: {
        props: this.props,
      },
    });
    return (
      <Box>
        <Text>Oops! We couldn&apos;t load your payment history. We&apos;re on it - please try again later.</Text>
      </Box>
    );
  };

  resendInviteToRenter = ({ tenantEmail }: Record<string, any>): void => {
    const { resendInviteToRenter } = this.props;
    const debouncedResend = debounce(resendInviteToRenter, DEBOUNCE_WAIT_TIME, { edges: ['leading'] });
    return debouncedResend({ tenantEmail });
  };

  requestRemindRenterPayRent = ({ tenantEmail }: Record<string, any>): void => {
    const { requestRemindRenterPayRent } = this.props;
    const debouncedResend = debounce(requestRemindRenterPayRent, DEBOUNCE_WAIT_TIME, { edges: ['leading'] });
    return debouncedResend({ tenantEmail });
  };

  remindTenantToPayMIC = ({ tenantEmail }: Record<string, any>): void => {
    const { remindTenantToPayMIC } = this.props;
    const debouncedResend = debounce(remindTenantToPayMIC, DEBOUNCE_WAIT_TIME, { edges: ['leading'] });
    return debouncedResend({ tenantEmail });
  };

  remindTenantToPayOneTimePayment = ({ tenantEmail, requestKey }: Record<string, any>): void => {
    const { remindTenantToPayOneTimePayment } = this.props;
    const debouncedResend = debounce(remindTenantToPayOneTimePayment, DEBOUNCE_WAIT_TIME, { edges: ['leading'] });
    return debouncedResend({ tenantEmail, requestKey });
  };

  render(): React.ReactNode {
    const {
      userEvent,
      featurePermissions,
      cancelOneTimePayment,
      goToCancelMoveInCostsModal,
      currentTerm,
      nextTerm,
      upcomingTransactions,
      completedTransactions,
      pastDueTransactions,
      listingAlias,
      goToSetupPaymentPlan,
      paymentPlanEnabled,
      rentSchedulePropertyId,
    } = this.props;
    const tabPanelProps = {
      userEvent,
      featurePermissions,
      resendInviteToRenter: this.resendInviteToRenter,
      requestRemindRenterPayRent: this.requestRemindRenterPayRent,
      remindTenantToPayMIC: this.remindTenantToPayMIC,
      remindTenantToPayOneTimePayment: this.remindTenantToPayOneTimePayment,
      cancelOneTimePayment,
      goToCancelMoveInCostsModal,
      currentTerm,
      nextTerm,
      listingAlias,
      goToSetupPaymentPlan,
      paymentPlanEnabled,
    };
    const isPaymentPlanRequested =
      upcomingTransactions?.some((transaction) => transaction.paymentType === ONE_TIME_PAYMENT_TYPE.PAYMENT_PLAN) ||
      completedTransactions?.some((transaction) => transaction.paymentType === ONE_TIME_PAYMENT_TYPE.PAYMENT_PLAN);
    const hasTransactions = Boolean(
      upcomingTransactions?.length || completedTransactions?.length || pastDueTransactions?.length,
    );

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text type="h2">Payment activity</Text>
          {hasTransactions && rentSchedulePropertyId && (
            <TransactionDownloadButton propertyId={rentSchedulePropertyId} />
          )}
        </div>
        <Box className="Dashboard-Box">
          <Tabs defaultIndex={this.initialTabIndex}>
            <TabList full borderBottom>
              {this.renderUpcomingTitle()}
              {this.renderCompletedTitle()}
              {this.renderPastDueTitle()}
            </TabList>
            <TabPanel>
              <TransactionRows category="upcoming" transactions={upcomingTransactions} {...tabPanelProps} />
            </TabPanel>
            <TabPanel>
              <TransactionRows transactions={completedTransactions} category="completed" {...tabPanelProps} />
            </TabPanel>
            <TabPanel>
              <TransactionRows
                transactions={pastDueTransactions}
                category="pastDue"
                isPaymentPlanRequested={isPaymentPlanRequested}
                {...tabPanelProps}
              />
            </TabPanel>
          </Tabs>
        </Box>
      </div>
    );
  }
}
export default LedgerSection;
